/**
 * LP quick navigation bar behavior (sections/medispa-lp-quick-navigation-bar.liquid):
 * builds nav items from .medispa-nav-anchor sections, smooth-scrolls to them,
 * and manages sticky attach/detach in coordination with the LP header's own
 * hide/reveal (via the --medispa-lp-header-visible-height CSS variable that the
 * section's <style> block consumes). Loaded by deferred-scripts.liquid
 * (marker: medispa-quick-navigation-bar).
 */
import { onDomReady } from '@/components/utils/medispa-lp';

function initQuickNav(quickNavWrapper: HTMLElement): void {
  const headerWrapper = document.querySelector<HTMLElement>('.medispa-lp-header-wrapper');
  const itemsContainer = quickNavWrapper.querySelector<HTMLElement>('.quick-nav-items');
  const anchors = Array.from(document.querySelectorAll<HTMLElement>('.medispa-nav-anchor'));

  if (itemsContainer) {
    itemsContainer.innerHTML = anchors
      .map((elem) => {
        const label = elem.getAttribute('data-anchor-label') || 'MISSING ANCHOR';
        return (
          '<a href="#" class="quick-nav-item" data-target="' +
          label +
          '"><span class="nav-item-text">' +
          label +
          '</span></a>'
        );
      })
      .join('');

    itemsContainer.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest('.quick-nav-item');
      if (!anchor) return;

      event.preventDefault();

      const targetLabel = anchor.getAttribute('data-target');
      if (!targetLabel) return;

      const target = document.querySelector('[data-anchor-label="' + targetLabel + '"]');
      if (!target) return;

      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      const isScrollingUp = targetTop < window.scrollY;
      const expectedHeaderHeight = isScrollingUp && headerWrapper ? headerWrapper.offsetHeight : 0;
      const stickyNavHeight =
        parseFloat(
          window
            .getComputedStyle(quickNavWrapper)
            .getPropertyValue('--medispa-lp-quick-nav-sticky-height'),
        ) || quickNavWrapper.offsetHeight;
      const stickyStackHeight = expectedHeaderHeight + stickyNavHeight + 24;

      window.scrollTo({ top: targetTop - stickyStackHeight, behavior: 'smooth' });
    });
  }

  const syncHeaderOffset = (): void => {
    let visibleHeaderHeight = 0;

    if (headerWrapper && !headerWrapper.classList.contains('is-hidden')) {
      visibleHeaderHeight = headerWrapper.offsetHeight;
    }

    quickNavWrapper.style.setProperty('--medispa-lp-header-visible-height', `${visibleHeaderHeight}px`);
  };

  const computeNaturalTop = (): number => {
    // Measure from natural document flow so a currently-stuck bar
    // doesn't report its stuck position as its original page position.
    quickNavWrapper.style.position = 'static';
    const top = quickNavWrapper.getBoundingClientRect().top + window.scrollY;
    quickNavWrapper.style.position = '';
    return top;
  };

  const computeStickyThreshold = (): number => {
    const stickyTop = parseFloat(window.getComputedStyle(quickNavWrapper).top) || 0;
    return naturalTop - stickyTop;
  };

  syncHeaderOffset();

  let naturalTop = computeNaturalTop();
  let stickyThreshold = computeStickyThreshold();
  let currentStatus: 'above' | 'below' = window.scrollY < stickyThreshold ? 'above' : 'below';

  const setSticky = (isSticky: boolean): void => {
    const nextStatus = isSticky ? 'below' : 'above';
    if (currentStatus === nextStatus) return;

    currentStatus = nextStatus;
    quickNavWrapper.classList.toggle('is-sticky', isSticky);
    document.body.dispatchEvent(
      new CustomEvent(
        isSticky ? 'medispa.quick-navigation.sticky-attach' : 'medispa.quick-navigation.sticky-detach',
      ),
    );
  };

  quickNavWrapper.classList.toggle('is-sticky', currentStatus === 'below');

  window.addEventListener('resize', () => {
    syncHeaderOffset();
    naturalTop = computeNaturalTop();
    stickyThreshold = computeStickyThreshold();
    setSticky(window.scrollY >= stickyThreshold);
  });

  if (headerWrapper) {
    new MutationObserver(() => {
      syncHeaderOffset();
      stickyThreshold = computeStickyThreshold();
      setSticky(window.scrollY >= stickyThreshold);
    }).observe(headerWrapper, { attributes: true, attributeFilter: ['class'] });
  }

  let lastScrollPos = window.scrollY;

  window.addEventListener(
    'scroll',
    () => {
      const newPos = window.scrollY;

      const scrollDirection =
        newPos - lastScrollPos === 0 ? '' : newPos - lastScrollPos < 0 ? 'up' : 'down';

      document.body.classList.toggle('scrolled-up', scrollDirection === 'up');

      stickyThreshold = computeStickyThreshold();
      setSticky(newPos >= stickyThreshold);

      lastScrollPos = newPos;
    },
    { passive: true },
  );
}

onDomReady(() => {
  const quickNavWrapper = document.querySelector<HTMLElement>('.medispa-quick-navigation-bar');
  if (quickNavWrapper) initQuickNav(quickNavWrapper);
});
